import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AlertController, LoadingController } from '@ionic/angular';
import { TRESPONSE_LOGIN } from '@monorepo/types';
import { PersistentStorageService } from '../persistent-storage/persistent-storage.service';
import { AuthEnvironmentService, ServiceEndPoints } from './auth-environment.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { FactoryService } from '@monorepo/core-data';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';

export type TDecodedAuthToken = {
  tenant_id: string;
  $int_perms: any[];
  sub: string;
  roles: any[];
  last_name: string;
  factory_id: string;
  user_code: string;
  usr: string;
  permissions: any[];
  name: string;
  $int_roles: any[];
  exp: number;
  iat: number;
  first_name: string;
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  #jwtHelper = new JwtHelperService();
  constructor(
    private http: HttpClient,
    private persistentStorageService: PersistentStorageService,
    private loadingController: LoadingController,
    @Inject(AuthEnvironmentService) private envProvider: AuthEnvironmentService,
    private factoryService: FactoryService,
    private alertCtrl: AlertController,
    private translate: TranslateService,
  ) {}

  #decodeJWT(token: string) {
    return this.#jwtHelper.decodeToken(token) as TDecodedAuthToken;
  }
  getDecodedJWTPromise() {
    return this.persistentStorageService.get('[LOGIN][AUTHENTICATION_TOKEN]').then((v) => {
      return this.#decodeJWT(v);
    });
  }

  async hasJwtTokenExpiered() {
    const token = await this.persistentStorageService.get('[LOGIN][AUTHENTICATION_TOKEN]');
    return this.#jwtHelper.isTokenExpired(token);
  }

  login(username: string, password: string) {
    this.persistentStorageService.remove('[LOGIN][AUTHENTICATION_TOKEN]');
    const body = new HttpParams().set('username', username).set('password', password);
    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    this.envProvider.getEnvVar(ServiceEndPoints.AUTHENTICATION_URL);
    const url = this.envProvider.getEnvVar(ServiceEndPoints.AUTHENTICATION_URL);
    return this.http.post<TRESPONSE_LOGIN>(url, body, { headers: headers });
  }

  async #showPopup(title: string, message: string) {
    const alert = await this.alertCtrl.create({
      header: title,
      subHeader: message,
      buttons: [
        {
          text: this.translate.instant('input_app_common.okay'),
        },
      ],
    });
    alert.present();
  }

  async logout() {
    const loading = await this.loadingController.create({
      message: this.translate.instant('input_app_auth_service.loggingOut'),
      backdropDismiss: false,
      keyboardClose: false,
    });
    await loading.present();
    lastValueFrom(this.factoryService.getHeartBeat())
      .then((res) => {
        console.log(res);
        this.persistentStorageService
          .remove('[LOGIN][AUTHENTICATION_TOKEN]')
          .then(async () => {
            await loading.dismiss();
            window.location.reload();
          })
          .catch(async () => {
            await loading.dismiss();
            this.#showPopup(
              this.translate.instant('input_app_common.error'),
              this.translate.instant('input_app_auth_service.logoutFailed'),
            );
            console.log('Error removing Auth token from cache, not allowed to logout');
          });
      })
      .catch(async (error) => {
        await loading.dismiss();
        this.#showPopup(
          this.translate.instant('input_app_common.warning'),
          this.translate.instant('input_app_auth_service.logoutNotAllowed'),
        );
        console.log(error, 'Error pop up to show Not allwed to logout. User neededs to be online');
      });
  }

  async isUserAuthenticated() {
    const authToken = await this.persistentStorageService.get('[LOGIN][AUTHENTICATION_TOKEN]');
    if (authToken) {
      return 'LOGGED_IN' as const;
    } else {
      return 'LOGGED_OUT' as const;
    }
  }
}
